import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * One row on the Cockpit Market Rates panel. Mirrors the `PanelRate` shape
 * returned by `GET /api/market-data/rates` (`PanelRate` in
 * `@arcanum-hq/agents`). apps/web intentionally does NOT import from agents;
 * the cross-domain contract is the API JSON, so the interface lives here and
 * the test in `use-market-rates.test.ts` locks in the field list.
 */
export interface MarketRate {
  rate_type: string;
  display_name: string;
  value: number;
  unit: '%';
  change_bps: number | null;
  as_of: string;
  source: 'fred' | 'derived';
  source_detail: string;
  source_url?: string;
  spark_data: number[];
}

export interface MarketRatesPayload {
  rates: MarketRate[];
  fetched_at: string;
  cache_hit: boolean;
  warnings: string[];
}

interface ApiMarketRatesResponse {
  success: boolean;
  data: MarketRatesPayload;
}

/**
 * Cockpit Market Rates panel — 7 rates (5 FRED-direct + 2 derived agency).
 * Server caches FRED observations 1h; the 5-minute client staleTime lets
 * admin spread overrides surface quickly (admin override → subsequent fetch
 * → live spread read → new derived value). refetchInterval keeps the panel
 * fresh across long idle sessions without burning network on mount.
 */
export function useMarketRates() {
  return useQuery<MarketRatesPayload>({
    queryKey: queryKeys.marketRates.panel(),
    queryFn: async () => {
      const res = await api.get<ApiMarketRatesResponse>('/api/market-data/rates');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000,
    refetchIntervalInBackground: false,
  });
}
